import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, Store } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import {
  ADMIN_ROUTE,
  MAGIC_CLICK_COUNT,
  MAGIC_CLICK_WINDOW_MS,
} from "@/lib/constants";
import { cartCount, CART_CHANGE_EVENT, getCart } from "@/lib/cart";
import { AccountChip, SignInButton } from "@/components/AccountChip";
import {
  CUSTOMER_CHANGE_EVENT,
  getCachedCustomer,
} from "@/lib/customerAuth";

export const Header = ({ onCartClick, onSignInClick, onMyOrdersClick, onReturnOrderClick, onProfileClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const onAdmin = location.pathname === ADMIN_ROUTE;

  const [count, setCount] = useState(0);
  const [customer, setCustomer] = useState(() => getCachedCustomer());
  useEffect(() => {
    const reload = () => setCount(cartCount(getCart()));
    const reloadCustomer = () => setCustomer(getCachedCustomer());
    reload();
    window.addEventListener(CART_CHANGE_EVENT, reload);
    window.addEventListener("storage", reload);
    window.addEventListener(CUSTOMER_CHANGE_EVENT, reloadCustomer);
    window.addEventListener("storage", reloadCustomer);
    return () => {
      window.removeEventListener(CART_CHANGE_EVENT, reload);
      window.removeEventListener("storage", reload);
      window.removeEventListener(CUSTOMER_CHANGE_EVENT, reloadCustomer);
      window.removeEventListener("storage", reloadCustomer);
    };
  }, []);

  // Magic-click easter egg — 5 quick clicks on the combined logo opens admin.
  const clicks = useRef([]);
  const handleLogoClick = (e) => {
    if (onAdmin) return;
    const now = Date.now();
    clicks.current = clicks.current.filter(
      (t) => now - t <= MAGIC_CLICK_WINDOW_MS,
    );
    clicks.current.push(now);
    if (clicks.current.length >= MAGIC_CLICK_COUNT) {
      e.preventDefault();
      clicks.current = [];
      navigate(ADMIN_ROUTE);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full bg-[#022C22] border-b border-[#E0B53C]/15 shadow-[0_6px_20px_rgba(0,0,0,0.18)]"
    >
      <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#E0B53C]/70 to-transparent" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex items-center justify-between gap-4 h-16 sm:h-20">
          <Link
            to="/"
            data-testid="header-logo"
            onClick={handleLogoClick}
            className="flex items-center gap-3 select-none group"
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
          >
            <BrandMark
              size={40}
              className="shrink-0 drop-shadow-[0_2px_6px_rgba(224,181,60,0.25)] transition-transform duration-300 group-hover:scale-[1.04] group-active:scale-95"
            />
            <span
              data-testid="header-wordmark"
              className="font-wordmark text-2xl sm:text-3xl leading-none tracking-tight"
            >
              <span style={{ color: "#E0B53C" }}>Masti</span>
              <span style={{ color: "#FFFFFF" }}>Bazaar</span>
            </span>
          </Link>

          <nav className="flex items-center gap-2">
            {!onAdmin && (
              customer ? (
                <AccountChip onMyOrders={onMyOrdersClick} onProfile={onProfileClick} />
              ) : (
                <SignInButton onClick={onSignInClick} />
              )
            )}
            {!onAdmin && onCartClick && (
              <button
                onClick={onCartClick}
                data-testid="header-cart-btn"
                aria-label="open cart"
                className="relative inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#E0B53C]/10 hover:bg-[#E0B53C]/20 text-[#E0B53C] transition-all"
              >
                <ShoppingCart size={18} strokeWidth={1.8} />
                {count > 0 && (
                  <span
                    data-testid="header-cart-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E0B53C] text-[#022C22] text-[10px] font-bold flex items-center justify-center shadow-md"
                  >
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </button>
            )}
            {onAdmin && (
              <Link
                to="/"
                data-testid="header-store-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-white hover:bg-white/10 rounded-full px-4 py-2 transition-all"
              >
                <Store size={15} strokeWidth={1.6} />
                <span className="hidden sm:inline">Storefront</span>
              </Link>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};
